import { create } from "zustand";
import { type ApiClient, runStoreAction } from "./shared";

export type ReportsApiType = "projecthours" | "workinghours";

export type ReportsBillableFilter = "billable" | "not_billable" | null;

export interface ReportsRangeInput {
	readonly end: string;
	readonly start: string;
}

export interface ProjecthoursReportRequest {
	readonly billable?: ReportsBillableFilter;
	readonly clients?: readonly string[];
	readonly completed?: boolean;
	readonly projects?: readonly string[];
	readonly range: ReportsRangeInput;
	readonly tags?: readonly string[];
	readonly tasks?: readonly string[];
	readonly type: "projecthours";
	readonly users: readonly string[] | "all";
}

export interface WorkinghoursReportRequest {
	readonly itemType?: readonly string[];
	readonly range: ReportsRangeInput;
	readonly rulebreaks?: boolean;
	readonly type: "workinghours";
	readonly users: readonly string[] | "all";
}

export type ReportsRequest = ProjecthoursReportRequest | WorkinghoursReportRequest;

export interface ReportsResponse<TItem = Record<string, unknown>> {
	readonly data: readonly TItem[];
}

export interface ReportsStoreState {
	readonly error: string | null;
	readonly isLoading: boolean;
	setError: (error: string | null) => void;
	setLoading: (isLoading: boolean) => void;
}

export const createReportsStore = (apiClient: ApiClient) => {
	const useStore = create<ReportsStoreState>((set) => ({
		isLoading: false,
		error: null,
		setLoading: (isLoading) => set({ isLoading }),
		setError: (error) => set({ error }),
	}));

	const fetchReport = async <TItem = Record<string, unknown>>(body: ReportsRequest): Promise<readonly TItem[]> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const response = await apiClient.post<ReportsResponse<TItem>>("/reports", body);
			return response?.data ?? [];
		});
	};

	const fetchProjecthours = async (
		body: Omit<ProjecthoursReportRequest, "type">
	): Promise<readonly Record<string, unknown>[]> => fetchReport({ ...body, type: "projecthours" });

	const fetchWorkinghours = async (
		body: Omit<WorkinghoursReportRequest, "type">
	): Promise<readonly Record<string, unknown>[]> => fetchReport({ ...body, type: "workinghours" });

	return {
		useStore,
		actions: {
			fetchReport,
			fetchProjecthours,
			fetchWorkinghours,
		},
	};
};
